/**
 * feat-107 Slice B — one inferred Question/Rehearsal/Reading (feat-343) card in importer Step 2.
 *
 * Presentational + controlled: the parent (PasteImportWizard) owns the working
 * model and passes the item, its live Needs-review flags, and change/drop
 * callbacks. Renders the type badge + type-override select, Move-to-Drill, Drop,
 * the editable body for the current type, and the amber "⚠ Needs review" chip
 * with each specific flag message. Mockup: paste-import-flow.html Step 2.
 *
 * Tokens are the REAL interviewer-frontend tailwind tokens (cream, white, coral,
 * coral-light, gold, success, warning, preview, danger, inkMuted, softBorder),
 * NOT the mockup's dash placeholders (which don't exist in tailwind.config.js).
 */

import React, { useState } from 'react';
import {
  QUESTION_TYPE_BADGE,
  REHEARSAL_BADGE,
  READING_BADGE,
  QUIZ_TERMS,
} from '../../quizTerminology';
import type { NeedsReviewFlag } from '../../../../services/notionCurriculumParser';
import {
  OPTION_LETTERS,
  resolveRole,
  ROLE_CHECKING_OFF,
  type ItemType,
  type WorkingItemType,
  type RoleCatalog,
  type WorkingDrill,
  type WorkingItem,
} from './importerModel';
import {
  NO_ACTIVE_PROMPT_VERSIONS_OPTION,
  ROLE_SELECT_ARIA_LABEL,
  ROLE_SELECT_PLACEHOLDER,
  truncateRole,
  unresolvedRolePlaceholder,
} from './roleRemapCopy';
import {
  ATTENTION_BORDER_LEFT,
  ATTENTION_INK,
  ATTENTION_MARK,
} from '../tracks/attentionClasses';
import {
  EditPreviewToggle,
  MarkdownPreview,
  READING_BODY_PLACEHOLDER,
  READING_BODY_SOFT_WARN_CHARS,
  READING_PREVIEW_PARITY_NOTE,
  readingCharCountText,
  type ReadingEditorMode,
} from '../readingMarkdown';
import { MC_OPTIONAL_SUFFIX } from '../../../../utils/mcOptions';

export interface InferredItemCardProps {
  item: WorkingItem;
  drills: WorkingDrill[];
  flags: NeedsReviewFlag[];
  onChange: (patch: Partial<WorkingItem>) => void;
  onDrop: () => void;
  /** feat-248 — the tenant's prompt-backed roles, or the fact that we don't
   *  know them yet / couldn't load them. Optional and defaulting to
   *  role-checking-off so a card rendered outside the wizard keeps today's
   *  free-text Role input. */
  roleCatalog?: RoleCatalog;
}

const TYPE_OPTIONS: { value: ItemType; label: string }[] = [
  { value: 'multiple_choice', label: QUESTION_TYPE_BADGE.multiple_choice },
  { value: 'fill_blank', label: QUESTION_TYPE_BADGE.fill_blank },
  { value: 'rehearsal', label: REHEARSAL_BADGE },
];

function badgeClasses(type: WorkingItemType): string {
  switch (type) {
    case 'reading':
      // feat-343: same chip the editor tree uses (DrillChildList).
      return 'bg-reading-light text-reading';
    case 'multiple_choice':
      return 'bg-blue-50 text-blue-700';
    case 'fill_blank':
      return 'bg-preview-light text-preview';
    case 'rehearsal':
      return 'bg-coral-light text-coral';
  }
}

function badgeLabel(type: WorkingItemType): string {
  if (type === 'reading') return READING_BADGE;
  return type === 'rehearsal'
    ? REHEARSAL_BADGE
    : QUESTION_TYPE_BADGE[type];
}

const inputCls =
  'w-full text-sm border border-softBorder rounded-lg px-2 py-1 focus:outline-none focus:ring-2 focus:ring-coral/40';

export function InferredItemCard({
  item,
  drills,
  flags,
  onChange,
  onDrop,
  roleCatalog = ROLE_CHECKING_OFF,
}: InferredItemCardProps): React.ReactElement {
  const blocking = flags.some((f) => f.severity === 'block');
  const needsReview = flags.length > 0;

  const changeType = (next: ItemType) => {
    const patch: Partial<WorkingItem> = { itemType: next };
    // Seed a rehearsal name from the question text so the switch isn't empty.
    if (next === 'rehearsal' && !item.name.trim() && item.questionText.trim()) {
      patch.name = item.questionText.trim();
    }
    onChange(patch);
  };

  const setOption = (idx: number, text: string) => {
    const mcOptions = item.mcOptions.map((o, i) =>
      i === idx ? { ...o, text } : o,
    );
    onChange({ mcOptions });
  };

  const confirmProposed = () => onChange({ proposedConfirmed: true });
  const dismissWarn = (reason: NeedsReviewFlag['reason']) =>
    onChange({ dismissed: [...item.dismissed, reason] });

  return (
    <div
      className={`bg-white border border-softBorder rounded-2xl p-3 ${
        needsReview ? `border-l-4 ${ATTENTION_BORDER_LEFT}` : ''
      }`}
      data-testid={`inferred-item-${item.tempId}`}
    >
      {/* Header: badge + type override · move · drop */}
      <div className="flex items-center justify-between gap-2 mb-2">
        {/* feat-248 §7: `flex-wrap` is REQUIRED, not cosmetic. At the real 976px
            wizard content width (max-w-5xl minus p-6) the "⚠ Needs review" chip
            renders 87.4px ON TOP OF the Move-to-Drill select — measured in the
            browser, not eyeballed. Pre-existing, but feat-248 makes that chip
            the COMMON case on rehearsal cards (every unresolved role earns
            one), so the exposure multiplies on the exact control this feature
            adds. Wrapping drops the chip to a second row; header 22px → 50px,
            nothing else moves. */}
        <div className="flex items-center gap-2 min-w-0 flex-wrap">
          <span className={`text-xs font-bold rounded-full px-2.5 py-0.5 ${badgeClasses(item.itemType)}`}>
            {badgeLabel(item.itemType)}
          </span>
          {/* feat-343: a Reading has no question/rehearsal fields to convert,
              so it gets no type-override select (design NOTES §2.2). */}
          {item.itemType !== 'reading' && (
            <select
              aria-label="Change item type"
              value={item.itemType}
              onChange={(e) => changeType(e.target.value as ItemType)}
              className="text-xs font-semibold bg-gray-100 rounded-full px-2 py-0.5 border-0 cursor-pointer"
            >
              {TYPE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          )}
          {needsReview && (
            <span className={`${ATTENTION_MARK} text-xs font-bold rounded-full px-2.5 py-0.5 whitespace-nowrap`}>
              ⚠ Needs review
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 text-xs shrink-0">
          <label className="sr-only" htmlFor={`move-${item.tempId}`}>Move to {QUIZ_TERMS.drill.singular}</label>
          <select
            id={`move-${item.tempId}`}
            aria-label={`Move to ${QUIZ_TERMS.drill.singular}`}
            value={item.drillTempId}
            onChange={(e) => onChange({ drillTempId: e.target.value })}
            className="text-inkMuted bg-transparent hover:text-coral cursor-pointer border-0 max-w-[140px] truncate"
            title={`Move to ${QUIZ_TERMS.drill.singular}`}
          >
            {drills.map((d) => (
              <option key={d.tempId} value={d.tempId}>
                {`→ ${d.title || QUIZ_TERMS.drill.singular}`}
              </option>
            ))}
          </select>
          <button type="button" onClick={onDrop} className="text-coral font-semibold hover:underline">
            Drop
          </button>
        </div>
      </div>

      {/* Body */}
      {item.itemType === 'reading' ? (
        <ReadingBody item={item} onChange={onChange} />
      ) : item.itemType === 'rehearsal' ? (
        <RehearsalBody item={item} onChange={onChange} roleCatalog={roleCatalog} />
      ) : item.itemType === 'fill_blank' ? (
        <FillBody item={item} onChange={onChange} onSetOption={setOption} onConfirmProposed={confirmProposed} />
      ) : (
        <McBody item={item} onChange={onChange} onSetOption={setOption} onConfirmProposed={confirmProposed} />
      )}

      {/* Flag messages */}
      {needsReview && (
        <ul className="mt-2 space-y-1">
          {flags.map((f, i) => (
            <li
              key={`${f.reason}-${i}`}
              className={`text-xs ${f.severity === 'block' ? ATTENTION_INK : 'text-inkMuted'}`}
            >
              ⚠ {f.message}
              {/* Only a `warn` is dismissible — a `block` is cleared by supplying
                  the field. feat-187: keyed off SEVERITY, not a reason allowlist,
                  so the new rehearsal_missing_correct_example warn gets the same
                  "looks fine" escape hatch fill_no_accept has. */}
              {f.severity === 'warn' && (
                <button
                  type="button"
                  onClick={() => dismissWarn(f.reason)}
                  className="ml-2 text-coral font-semibold hover:underline"
                >
                  Dismiss
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      {/* a11y: surface the blocking state for the wizard's gate aria */}
      {blocking && <span className="sr-only" data-testid="item-blocks-commit" />}
    </div>
  );
}

// ---- feat-343: Reading body (title + markdown body with Edit/Preview) ----

function ReadingBody({
  item,
  onChange,
}: {
  item: WorkingItem;
  onChange: (patch: Partial<WorkingItem>) => void;
}): React.ReactElement {
  // Chosen ONCE per card: Preview when there is something to preview, Edit when
  // the body is empty (the fix is typing). Never flips on its own afterwards.
  const [mode, setMode] = useState<ReadingEditorMode>(() =>
    item.readingBody.trim() ? 'preview' : 'edit',
  );
  const body = item.readingBody;
  const emptyBody = !body.trim();
  const overSoftCap = body.length > READING_BODY_SOFT_WARN_CHARS;
  return (
    <div className="space-y-2">
      <input
        aria-label="Reading title"
        value={item.readingTitle}
        onChange={(e) => onChange({ readingTitle: e.target.value })}
        placeholder="Reading title (optional)"
        className={`${inputCls} font-semibold`}
      />
      <div>
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs text-inkMuted">Body (Markdown)</span>
          <EditPreviewToggle mode={mode} onChange={setMode} />
        </div>
        {mode === 'edit' ? (
          <textarea
            aria-label="Reading body (Markdown)"
            value={body}
            onChange={(e) => onChange({ readingBody: e.target.value })}
            placeholder={READING_BODY_PLACEHOLDER}
            className={`w-full text-[12.5px] font-mono leading-relaxed border border-softBorder rounded-lg px-2 py-1.5 min-h-[160px] max-h-[320px] resize-y focus:outline-none focus:ring-2 focus:ring-coral/40 ${
              emptyBody ? 'ring-2 ring-coral/40' : ''
            }`}
          />
        ) : (
          <div
            className="rounded-lg border border-softBorder bg-white px-3 py-2 overflow-y-auto max-h-[320px] break-words"
            data-testid={`reading-preview-${item.tempId}`}
          >
            <MarkdownPreview source={body} />
            <p className="mt-3 pt-2 border-t border-softBorder/60 text-[10px] text-inkMuted">
              {READING_PREVIEW_PARITY_NOTE}
            </p>
          </div>
        )}
        <div className={`mt-1 text-[11px] ${overSoftCap ? `${ATTENTION_INK} font-semibold` : 'text-inkMuted'}`}>
          {readingCharCountText(body.length)}
        </div>
      </div>
    </div>
  );
}

// ---- shared: A–D option rows (MC + fill) ----

function OptionRows({
  item,
  onChange,
  onSetOption,
  onConfirmProposed,
}: {
  item: WorkingItem;
  onChange: (patch: Partial<WorkingItem>) => void;
  onSetOption: (idx: number, text: string) => void;
  onConfirmProposed: () => void;
}): React.ReactElement {
  const hasProposed = item.mcOptions.some((o) => o.proposed && o.text.trim());
  return (
    <div>
      <ul className="space-y-1">
        {item.mcOptions.map((opt, idx) => {
          const isCorrect = item.correctIndex === idx;
          return (
            <li
              key={idx}
              className={`flex items-center gap-2 rounded-lg px-2 py-1 ${
                isCorrect ? 'bg-success-light' : ''
              } ${opt.proposed && !item.proposedConfirmed ? 'bg-warning-surface/60' : ''}`}
            >
              <input
                type="radio"
                name={`correct-${item.tempId}`}
                checked={isCorrect}
                onChange={() => onChange({ correctIndex: idx })}
                aria-label={`Mark option ${OPTION_LETTERS[idx].toUpperCase()} correct`}
                className="accent-success"
              />
              <span className={`w-4 text-xs font-bold ${isCorrect ? 'text-success' : 'text-inkMuted'}`}>
                {OPTION_LETTERS[idx].toUpperCase()}
                {isCorrect ? ' ✓' : ''}
              </span>
              <input
                value={opt.text}
                onChange={(e) => onSetOption(idx, e.target.value)}
                className="flex-1 text-sm bg-transparent border border-transparent hover:border-softBorder focus:border-softBorder rounded px-1 py-0.5 focus:outline-none"
                placeholder={`Option ${OPTION_LETTERS[idx].toUpperCase()}${
                  // feat-346: MC needs only A + B; C/D are optional.
                  item.itemType === 'multiple_choice' && idx >= 2 ? MC_OPTIONAL_SUFFIX : ''
                }`}
              />
            </li>
          );
        })}
      </ul>
      {hasProposed && !item.proposedConfirmed && (
        <button
          type="button"
          onClick={onConfirmProposed}
          className="mt-1.5 text-xs text-coral font-semibold hover:underline"
        >
          Confirm the assigned letter
        </button>
      )}
    </div>
  );
}

// ---- shared: right-answer praise + wrong-answer explanation (MC + fill) ----

function PraiseExplainFields({
  item,
  onChange,
}: {
  item: WorkingItem;
  onChange: (patch: Partial<WorkingItem>) => void;
}): React.ReactElement {
  return (
    <div className="grid sm:grid-cols-2 gap-2 mt-2">
      <label className="text-xs text-inkMuted">
        Right-answer praise
        <textarea
          aria-label="Right-answer praise"
          value={item.correctExplanation}
          onChange={(e) => onChange({ correctExplanation: e.target.value })}
          rows={2}
          className={`${inputCls} mt-0.5 resize-y`}
          placeholder="Celebrate in one sentence."
        />
      </label>
      <label className="text-xs text-inkMuted">
        Wrong-answer explanation
        <textarea
          aria-label="Wrong-answer explanation"
          value={item.explanation}
          onChange={(e) => onChange({ explanation: e.target.value })}
          rows={2}
          className={`${inputCls} mt-0.5 resize-y`}
          placeholder="What to do differently, stated warmly."
        />
      </label>
    </div>
  );
}

// ---- MC body ----

function McBody({
  item,
  onChange,
  onSetOption,
  onConfirmProposed,
}: {
  item: WorkingItem;
  onChange: (patch: Partial<WorkingItem>) => void;
  onSetOption: (idx: number, text: string) => void;
  onConfirmProposed: () => void;
}): React.ReactElement {
  return (
    <div>
      <input
        aria-label="Question text"
        value={item.questionText}
        onChange={(e) => onChange({ questionText: e.target.value })}
        className={`${inputCls} font-semibold mb-2`}
        placeholder="Question text"
      />
      <OptionRows item={item} onChange={onChange} onSetOption={onSetOption} onConfirmProposed={onConfirmProposed} />
      <PraiseExplainFields item={item} onChange={onChange} />
    </div>
  );
}

// ---- Fill body ----

function FillBody({
  item,
  onChange,
  onSetOption,
  onConfirmProposed,
}: {
  item: WorkingItem;
  onChange: (patch: Partial<WorkingItem>) => void;
  onSetOption: (idx: number, text: string) => void;
  onConfirmProposed: () => void;
}): React.ReactElement {
  return (
    <div className="space-y-2">
      <input
        aria-label="Question text"
        value={item.questionText}
        onChange={(e) => onChange({ questionText: e.target.value })}
        className={`${inputCls} font-semibold`}
        placeholder="Question text (use ___ for the blank)"
      />
      <div className="text-xs text-inkMuted">
        Options — the correct one is the canonical answer that fills the blank.
      </div>
      <OptionRows item={item} onChange={onChange} onSetOption={onSetOption} onConfirmProposed={onConfirmProposed} />
      <label className="text-xs text-inkMuted block">
        Also accept (one per line) — extra spellings/synonyms, beyond the correct option
        <textarea
          aria-label="Accepted variants"
          value={item.fillVariants}
          onChange={(e) => onChange({ fillVariants: e.target.value })}
          rows={2}
          className={`${inputCls} mt-0.5 resize-y`}
          placeholder={'friendly\ngenuine'}
        />
      </label>
      <PraiseExplainFields item={item} onChange={onChange} />
    </div>
  );
}

// ---- Rehearsal body ----

/**
 * feat-248 §3 — the role control. SIX variants, one component.
 *
 * The `<select>` is not an invention: the MANUAL rehearsal editor has rendered
 * exactly this since it was written (`QuizContentManagement.tsx:1330-1347`),
 * over the same `listRolesWithActivePrompt` list, disabled with the same
 * message when the tenant has none. The importer was the single surface that
 * bypassed it and sent free text.
 *
 * ONE DELIBERATE DIVERGENCE, DO NOT "FIX" IT BACK: the manual editor defaults
 * an unset role to `availableRoles[0]` (`QuizContentManagement.tsx:1139`). The
 * importer must NOT. Silently picking the first available role would send a
 * wrong-but-VALID role to the backend, which would then grade the trainee
 * against another role's prompt — a wrong answer with no error anywhere. A
 * visible block beats a silent wrong answer, so an unresolved role stays
 * unresolved until a human picks. (Notes §9.)
 *
 * | # | when                | ring | why                                    |
 * |---|---------------------|------|----------------------------------------|
 * | 1 | resolved            | no   | nothing to fix                         |
 * | 2 | present, unresolved | YES  | fix this field, here                   |
 * | 3 | absent              | YES  | ditto (existing rehearsal_missing_role)|
 * | 4 | tenant has 0 roles  | no   | the user CANNOT fix it here            |
 * | 5 | roles loading       | no   | ditto — and nothing is wrong yet       |
 * | 6 | fetch failed        | n/a  | stays the free-text <input>            |
 *
 * Variants 4 and 5 get NO ring on purpose: a ring means "fix this field, here",
 * and in both cases the user can't. Variant 4's blocking signal is carried by
 * the left stripe, the chip, the flag line and the banner — four channels, none
 * of which promise a fix that isn't available.
 */
function RoleControl({
  item,
  onChange,
  roleCatalog,
}: {
  item: WorkingItem;
  onChange: (patch: Partial<WorkingItem>) => void;
  roleCatalog: RoleCatalog;
}): React.ReactElement {
  const pasted = item.role.trim();
  const base = `${inputCls} mt-0.5`;
  const enabledCls = 'bg-white cursor-pointer';
  // The file's EXISTING "this field is why you're blocked" treatment
  // (the empty-role ring below) — reused, not invented.
  const blockRing = 'ring-2 ring-coral/40';
  const disabledCls = 'bg-gray-100 text-inkMuted cursor-not-allowed';

  // Variant 6 — the fetch failed. Role checking is off for this import, so the
  // control goes back to exactly what shipped before: free text, sent verbatim.
  // Substituting a select here would be a lie about what we can validate.
  if (roleCatalog.status === 'failed') {
    return (
      <input
        aria-label={ROLE_SELECT_ARIA_LABEL}
        value={item.role}
        onChange={(e) => onChange({ role: e.target.value })}
        className={`${base} ${!pasted ? blockRing : ''}`}
        placeholder="add a role…"
      />
    );
  }

  // Variant 5 — still loading. The list is UNKNOWN, not empty: show what was
  // pasted, disabled, and raise nothing.
  //
  // §3 says "the pasted value as-is", meaning it is not replaced by a
  // placeholder MESSAGE — but §5.5's length rule still binds, because an
  // `<option>` cannot be truncated with CSS and a 500-character role would
  // stretch the select past the card. Truncation happens in the string or not
  // at all.
  if (roleCatalog.status === 'loading') {
    return (
      <select aria-label={ROLE_SELECT_ARIA_LABEL} value="pending" disabled className={`${base} ${disabledCls}`}>
        <option value="pending">{truncateRole(pasted)}</option>
      </select>
    );
  }

  const roles = roleCatalog.roles;

  // Variant 4 — the tenant has no role with an active prompt template. Option
  // text is VERBATIM from the manual editor (`QuizContentManagement.tsx:1338`);
  // two surfaces saying the same thing differently is how a product grows
  // dialects.
  if (roles.length === 0) {
    return (
      <select aria-label={ROLE_SELECT_ARIA_LABEL} value="none" disabled className={`${base} ${disabledCls}`}>
        <option value="none">{NO_ACTIVE_PROMPT_VERSIONS_OPTION}</option>
      </select>
    );
  }

  const resolved = resolveRole(item.role, roles);
  const options = roles.map((r) => (
    <option key={r} value={r}>{r}</option>
  ));

  // Variant 1 — resolved. The select carries the role_key, which is what the
  // commit payload will send.
  if (resolved) {
    return (
      <select
        aria-label={ROLE_SELECT_ARIA_LABEL}
        value={resolved}
        onChange={(e) => onChange({ role: e.target.value })}
        className={`${base} ${enabledCls}`}
      >
        {options}
      </select>
    );
  }

  // Variants 2 and 3 — unresolved or absent. The placeholder is what tells them
  // apart on screen, and it is `disabled` so it cannot be re-selected once left.
  return (
    <select
      aria-label={ROLE_SELECT_ARIA_LABEL}
      value=""
      onChange={(e) => onChange({ role: e.target.value })}
      className={`${base} ${enabledCls} ${blockRing}`}
    >
      <option value="" disabled>
        {pasted ? unresolvedRolePlaceholder(pasted) : ROLE_SELECT_PLACEHOLDER}
      </option>
      {options}
    </select>
  );
}

function RehearsalBody({
  item,
  onChange,
  roleCatalog,
}: {
  item: WorkingItem;
  onChange: (patch: Partial<WorkingItem>) => void;
  roleCatalog: RoleCatalog;
}): React.ReactElement {
  const good = item.criteria.filter((c) => c.kind === 'good').length;
  const bad = item.criteria.filter((c) => c.kind === 'bad').length;
  const exampleMissing = !item.correctExample.trim();
  return (
    <div className="space-y-2">
      <input
        aria-label="Rehearsal name"
        value={item.name}
        onChange={(e) => onChange({ name: e.target.value })}
        className={`${inputCls} font-semibold`}
        placeholder="Short rehearsal name"
      />
      <div className="grid sm:grid-cols-2 gap-2 text-xs">
        {/* feat-248: the `*` marker span lives inside this <label>, so the
            select's accessible name would otherwise be "Role *" — hence the
            explicit aria-label inside RoleControl. */}
        <label className="text-inkMuted">
          Role <span className="text-coral">*</span>
          <RoleControl item={item} onChange={onChange} roleCatalog={roleCatalog} />
        </label>
        <div className="text-inkMuted self-end pb-1">
          <span className="font-bold text-success">Good</span> · {good} &nbsp;
          <span className="font-bold text-coral">Bad</span> · {bad}
        </div>
      </div>
      <label className="text-xs text-inkMuted block">
        Scenario
        <textarea
          value={item.scenarioText}
          onChange={(e) => onChange({ scenarioText: e.target.value })}
          rows={2}
          className={`${inputCls} mt-0.5 resize-y italic`}
          placeholder="Describe the situation the trainee should role-play."
        />
      </label>
      {/* feat-187 (R5): `correct_example_response` — same field the Lesson editor
          calls "Correct example response". Empty ⇒ the warn chip above. */}
      <label className="text-xs text-inkMuted block">
        Correct example response
        <textarea
          aria-label="Correct example response"
          value={item.correctExample}
          onChange={(e) => onChange({ correctExample: e.target.value })}
          rows={2}
          className={`${inputCls} mt-0.5 resize-y ${exampleMissing ? 'ring-2 ring-warning-on/40' : ''}`}
          placeholder="What a great answer sounds like — shown to the trainee as “Try saying…”."
        />
      </label>
    </div>
  );
}

export default InferredItemCard;
